import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalAccessibility } from '../../hooks/useModalAccessibility';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';
import type { Token, Prices, FIFOResults } from '../../types';

interface InvestmentSimulatorProps {
  isOpen: boolean;
  onClose: () => void;
  tokens: Token[];
  prices: Prices;
  fifoResults: FIFOResults;
}

interface TokenSimulation {
  tokenId: string;
  displayName: string;
  // État actuel
  currentQty: number;
  currentAvgCost: number;
  currentPrice: number;
  currentTotalCost: number;
  currentValue: number;
  currentPL: number;
  currentPLPct: number;
  // État après simulation (investissement + hausse)
  newQty: number;
  newTotalQty: number;
  newTotalCost: number;
  newAvgCost: number;
  newPrice: number;
  newValue: number;
  newPL: number;
  newPLPct: number;
  // Métriques de décision
  recoversToProfit: boolean;      // Revient-on en positif ?
  recoveryGap: number;            // Combien manque-t-il pour être à 0 (si négatif)
  profitGain: number;             // Combien de profit supplémentaire
  efficiencyScore: number;        // Score d'efficacité (plus haut = meilleur investissement)
}

type SortKey = 'efficiency' | 'currentPL' | 'newPL' | 'name';

/**
 * InvestmentSimulator - Simulateur d'investissement avec hausse cible
 *
 * Permet de simuler un investissement sur chaque token du portfolio
 * et de voir l'impact d'une hausse de X% après l'achat.
 * Aide à déterminer le meilleur token sur lequel investir pour
 * maximiser la récupération des pertes.
 */
export function InvestmentSimulator({
  isOpen,
  onClose,
  tokens,
  prices,
  fifoResults
}: InvestmentSimulatorProps) {
  const { t } = useTranslation();
  const [investmentAmount, setInvestmentAmount] = useState<string>('1000');
  const [targetRise, setTargetRise] = useState<string>('20');
  const [sortBy, setSortBy] = useState<SortKey>('efficiency');
  const [showOnlyNegative, setShowOnlyNegative] = useState<boolean>(true);

  const modalRef = useModalAccessibility(isOpen, onClose);

  // Calcul des simulations pour chaque token
  const simulations = useMemo((): TokenSimulation[] => {
    const amount = parseFloat(investmentAmount) || 0;
    const rise = parseFloat(targetRise) || 0;

    if (amount <= 0 || tokens.length === 0) return [];

    return tokens.map(token => {
      const fifo = fifoResults[token.id] || {};
      const currentPrice = prices[token.id]?.usd || 0;
      const currentQty = fifo.remainingQty || 0;
      const currentAvgCost = fifo.avgCost || 0;
      const currentTotalCost = currentQty * currentAvgCost;
      const currentValue = currentQty * currentPrice;
      const currentPL = currentValue - currentTotalCost;
      const currentPLPct = currentTotalCost > 0 ? (currentPL / currentTotalCost) * 100 : 0;

      // Simulation : investir le montant au prix actuel
      const newQty = currentPrice > 0 ? amount / currentPrice : 0;
      const newTotalQty = currentQty + newQty;
      const newTotalCost = currentTotalCost + amount;
      const newAvgCost = newTotalQty > 0 ? newTotalCost / newTotalQty : 0;

      // Simulation : prix après la hausse cible
      const newPrice = currentPrice * (1 + rise / 100);
      const newValue = newTotalQty * newPrice;
      const newPL = newValue - newTotalCost;
      const newPLPct = newTotalCost > 0 ? (newPL / newTotalCost) * 100 : 0;

      // Métriques de décision
      const recoversToProfit = newPL >= 0;
      const recoveryGap = newPL < 0 ? Math.abs(newPL) : 0;
      const profitGain = newPL - currentPL;

      // Score d'efficacité :
      // - Si on passe en positif : bonus important
      // - Sinon : progression relative vers le breakeven
      let efficiencyScore = 0;
      if (recoversToProfit && currentPL < 0) {
        // Passage de négatif à positif : score max + profit gagné
        efficiencyScore = 1000 + newPL;
      } else if (recoversToProfit) {
        // Déjà positif, on regarde le gain
        efficiencyScore = profitGain;
      } else {
        // Toujours négatif : on regarde le % de récupération
        const totalLoss = Math.abs(currentPL);
        const recovered = profitGain;
        efficiencyScore = totalLoss > 0 ? (recovered / totalLoss) * 100 : 0;
      }

      return {
        tokenId: token.id,
        displayName: token.displayName || token.id,
        currentQty,
        currentAvgCost,
        currentPrice,
        currentTotalCost,
        currentValue,
        currentPL,
        currentPLPct,
        newQty,
        newTotalQty,
        newTotalCost,
        newAvgCost,
        newPrice,
        newValue,
        newPL,
        newPLPct,
        recoversToProfit,
        recoveryGap,
        profitGain,
        efficiencyScore
      };
    }).filter(sim => {
      // Filtrer les tokens sans position ou sans prix
      if (sim.currentQty <= 0 || sim.currentPrice <= 0) return false;
      // Filtrer par P/L négatif si option activée
      if (showOnlyNegative && sim.currentPL >= 0) return false;
      return true;
    });
  }, [tokens, prices, fifoResults, investmentAmount, targetRise, showOnlyNegative]);

  // Tri des résultats
  const sortedSimulations = useMemo(() => {
    return [...simulations].sort((a, b) => {
      switch (sortBy) {
        case 'efficiency':
          return b.efficiencyScore - a.efficiencyScore;
        case 'currentPL':
          return a.currentPL - b.currentPL; // Plus négatif en premier
        case 'newPL':
          return b.newPL - a.newPL; // Plus positif en premier
        case 'name':
          return a.displayName.localeCompare(b.displayName);
        default:
          return 0;
      }
    });
  }, [simulations, sortBy]);

  // Statistiques globales
  const stats = useMemo(() => {
    const tokensRecovering = simulations.filter(s => s.recoversToProfit && s.currentPL < 0).length;
    const tokensInLoss = simulations.filter(s => s.currentPL < 0).length;
    const bestToken = sortedSimulations[0];
    return { tokensRecovering, tokensInLoss, bestToken };
  }, [simulations, sortedSimulations]);

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay show"
      onClick={(e: React.MouseEvent<HTMLDivElement>) =>
        (e.target as HTMLElement).className === 'modal-overlay show' && onClose()
      }
      role="dialog"
      aria-modal="true"
      aria-labelledby="investment-simulator-title"
    >
      <div
        className="modal-content"
        ref={modalRef}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className="modal-header" id="investment-simulator-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.TrendingUp size={18} />
            {t('investmentSimulator.title', 'Simulateur de Récupération')}
          </div>
          <button
            className="btn-ghost"
            onClick={onClose}
            style={{ padding: '4px' }}
            aria-label={t('common.close', 'Fermer')}
          >
            <Icons.X size={18} />
          </button>
        </div>

        {/* Description */}
        <div style={{
          padding: '12px 16px',
          background: 'var(--bg)',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '13px',
          color: 'var(--muted)'
        }}>
          <p style={{ margin: 0 }}>
            {t('investmentSimulator.description',
              "Simulez un investissement sur chaque token et voyez l'impact d'une hausse. " +
              "Identifiez le meilleur token pour maximiser vos chances de revenir en positif."
            )}
          </p>
        </div>

        {/* Paramètres */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '12px', marginBottom: '4px' }}>
              {t('investmentSimulator.investmentAmount', 'Montant à investir ($)')}
            </label>
            <input
              type="number"
              min="0"
              step="100"
              value={investmentAmount}
              onChange={(e) => setInvestmentAmount(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '12px', marginBottom: '4px' }}>
              {t('investmentSimulator.targetRise', 'Hausse cible (%)')}
            </label>
            <input
              type="number"
              min="0"
              max="1000"
              step="5"
              value={targetRise}
              onChange={(e) => setTargetRise(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '12px', marginBottom: '4px' }}>
              {t('investmentSimulator.sortBy', 'Trier par')}
            </label>
            <select
              aria-label={t('investmentSimulator.sortBy', 'Trier par')}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortKey)}
              style={{ width: '100%' }}
            >
              <option value="efficiency">{t('investmentSimulator.sortEfficiency', 'Efficacité')}</option>
              <option value="currentPL">{t('investmentSimulator.sortCurrentPL', 'Perte actuelle')}</option>
              <option value="newPL">{t('investmentSimulator.sortNewPL', 'P/L après simulation')}</option>
              <option value="name">{t('investmentSimulator.sortName', 'Nom')}</option>
            </select>
          </div>

          <div className="form-group" style={{ margin: 0, display: 'flex', alignItems: 'flex-end' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={showOnlyNegative}
                onChange={(e) => setShowOnlyNegative(e.target.checked)}
              />
              {t('investmentSimulator.onlyNegative', 'Uniquement les tokens en perte')}
            </label>
          </div>
        </div>

        {/* Statistiques résumées */}
        {simulations.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '12px 16px',
              background: 'var(--bg)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('investmentSimulator.tokensAnalyzed', 'Tokens analysés')}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                {simulations.length}
              </div>
            </div>

            <div style={{
              padding: '12px 16px',
              background: 'var(--bg)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('investmentSimulator.wouldRecover', 'Reviendraient en positif')}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: stats.tokensRecovering > 0 ? '#10b981' : '#ef4444' }}>
                {stats.tokensRecovering} / {stats.tokensInLoss}
              </div>
            </div>

            {stats.bestToken && (
              <div style={{
                padding: '12px 16px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))',
                borderRadius: '8px',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                  {t('investmentSimulator.recommended', 'Meilleur investissement')}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>
                  {stats.bestToken.displayName}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tableau des résultats */}
        {sortedSimulations.length > 0 ? (
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Token</th>
                  <th>Prix actuel</th>
                  <th>P/L actuel</th>
                  <th>P/L % actuel</th>
                  <th style={{ borderLeft: '2px solid var(--accent)', paddingLeft: '12px' }}>
                    Prix +{targetRise}%
                  </th>
                  <th>Nouveau P/L</th>
                  <th>Nouveau P/L %</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {sortedSimulations.map((sim, index) => (
                  <tr
                    key={sim.tokenId}
                    style={{
                      background: index === 0
                        ? 'linear-gradient(90deg, rgba(16, 185, 129, 0.1), transparent)'
                        : undefined
                    }}
                  >
                    <td style={{ fontWeight: index === 0 ? 700 : 400 }}>
                      {index + 1}
                      {index === 0 && ' ★'}
                    </td>
                    <td style={{ textAlign: 'left', fontWeight: 600 }}>
                      {sim.displayName}
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
                        +{fmt(sim.newQty, 6)} unités
                      </div>
                    </td>
                    <td>{fmt(sim.currentPrice, 2)} $</td>
                    <td className={sim.currentPL >= 0 ? 'positive' : 'negative'}>
                      {sim.currentPL >= 0 ? '+' : ''}{fmt(sim.currentPL, 0)} $
                    </td>
                    <td className={sim.currentPLPct >= 0 ? 'positive' : 'negative'}>
                      {sim.currentPLPct >= 0 ? '+' : ''}{fmt(sim.currentPLPct, 1)}%
                    </td>
                    <td style={{ borderLeft: '2px solid var(--accent)', paddingLeft: '12px' }}>
                      {fmt(sim.newPrice, 2)} $
                    </td>
                    <td className={sim.newPL >= 0 ? 'positive' : 'negative'}>
                      {sim.newPL >= 0 ? '+' : ''}{fmt(sim.newPL, 0)} $
                    </td>
                    <td className={sim.newPLPct >= 0 ? 'positive' : 'negative'}>
                      {sim.newPLPct >= 0 ? '+' : ''}{fmt(sim.newPLPct, 1)}%
                    </td>
                    <td>
                      {sim.recoversToProfit ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          background: 'rgba(16, 185, 129, 0.15)',
                          color: '#10b981',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          <Icons.TrendingUp size={12} />
                          {t('investmentSimulator.profit', 'PROFIT')}
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          background: 'rgba(239, 68, 68, 0.15)',
                          color: '#ef4444',
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}>
                          -{fmt(sim.recoveryGap, 0)} $
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: 'var(--muted)'
          }}>
            {tokens.length === 0 ? (
              <p>{t('investmentSimulator.noTokens', 'Aucun token dans le portfolio')}</p>
            ) : showOnlyNegative ? (
              <p>{t('investmentSimulator.noLossTokens', 'Aucun token en perte ! Votre portfolio est en positif.')}</p>
            ) : (
              <p>{t('investmentSimulator.noData', 'Aucune donnée disponible')}</p>
            )}
          </div>
        )}

        {/* Explication de l'algorithme */}
        <div style={{
          marginTop: '20px',
          padding: '12px 16px',
          background: 'var(--bg)',
          borderRadius: '8px',
          fontSize: '11px',
          color: 'var(--muted)'
        }}>
          <strong style={{ color: 'var(--text)' }}>
            {t('investmentSimulator.howItWorks', 'Comment ça marche ?')}
          </strong>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px' }}>
            <li>
              {t('investmentSimulator.explanation1',
                "Pour chaque token, on simule l'achat de {amount}$ au prix actuel, puis une hausse de {rise}%."
              ).replace('{amount}', investmentAmount).replace('{rise}', targetRise)}
            </li>
            <li>
              {t('investmentSimulator.explanation2',
                "Le score d'efficacité privilégie les tokens qui passent de négatif à positif avec cette simulation."
              )}
            </li>
            <li>
              {t('investmentSimulator.explanation3',
                "Un token avec une grosse perte (-70%) nécessite une hausse bien plus importante pour revenir à zéro qu'un token à -40%."
              )}
            </li>
          </ul>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ marginTop: '20px' }}>
          <button className="btn-ghost" onClick={onClose}>
            {t('common.close', 'Fermer')}
          </button>
        </div>
      </div>
    </div>
  );
}
